<script setup lang="ts">
import { just, nothing } from "../../lib/maybe";
import { ref } from "vue";
import ThemeToggle from "../ThemeToggle.vue";
import ZwButton from "./ZwButton.vue";
import ZwCheckbox from "./ZwCheckbox.vue";
import ZwDialog from "./ZwDialog.vue";
import ZwFieldRow from "./ZwFieldRow.vue";
import ZwInput from "./ZwInput.vue";
import ZwMenu from "./ZwMenu.vue";
import ZwMenuItem from "./ZwMenuItem.vue";
import ZwMenuSeparator from "./ZwMenuSeparator.vue";
import ZwSelect from "./ZwSelect.vue";
import ZwTextarea from "./ZwTextarea.vue";

const sections = [
  { id: "foundations", label: "Foundations" },
  { id: "selection", label: "Selection" },
  { id: "buttons", label: "Buttons" },
  { id: "forms", label: "Forms" },
  { id: "menus", label: "Menus" },
  { id: "dialogs", label: "Dialogs" },
] as const;

const title = ref("Warning Shot");
const subtitle = ref("");
const notes = ref("Bananas are good.");
const showTitle = ref(true);
const showAuthor = ref(false);
const printSize = ref("trade");
const menuOpen = ref(false);
const dialogOpen = ref(false);
const dialogName = ref("Chapter 1");

const printOptions = [
  { value: "a5", label: "A5" },
  { value: "trade", label: "Trade (6 × 9 in)" },
  { value: "letter", label: "US Letter" },
];

const colorTokens = [
  { name: "--bg", swatch: "var(--bg)", group: "Surface" },
  { name: "--bg-raised", swatch: "var(--bg-raised)", group: "Surface" },
  { name: "--text", swatch: "var(--text)", group: "Ink" },
  { name: "--text-strong", swatch: "var(--text-strong)", group: "Ink" },
  { name: "--text-muted", swatch: "var(--text-muted)", group: "Ink" },
  { name: "--line", swatch: "var(--line)", group: "Structure" },
  { name: "--code-bg", swatch: "var(--code-bg)", group: "Structure" },
  { name: "--hover-fill", swatch: "var(--hover-fill)", group: "Structure" },
  { name: "--backdrop", swatch: "var(--backdrop)", group: "Structure" },
  {
    name: "--color-zw-amber (copper)",
    swatch: "var(--color-zw-amber)",
    group: "Accent",
  },
  {
    name: "--color-zw-amber-soft (copper)",
    swatch: "var(--color-zw-amber-soft)",
    group: "Accent",
  },
  { name: "--selection", swatch: "var(--selection)", group: "Writing" },
  { name: "--comment", swatch: "var(--comment)", group: "Writing" },
  { name: "--comment-wash", swatch: "var(--comment-wash)", group: "Writing" },
  { name: "--danger", swatch: "var(--danger)", group: "Status" },
  { name: "--splash-title", swatch: "var(--splash-title)", group: "Brand" },
];
</script>

<template>
  <div class="ui-gallery zw-paper-surface">
    <aside class="ui-gallery-nav" aria-label="Component library">
      <div class="ui-gallery-brand">
        <p class="ui-gallery-kicker">Zephyrwrite</p>
        <h1>UI library</h1>
        <p class="ui-gallery-lede">
          Live tokens and primitives. Open with
          <code>?ui</code>.
        </p>
      </div>
      <nav>
        <a
          v-for="section in sections"
          :key="section.id"
          class="ui-gallery-nav-link"
          :href="`#${section.id}`"
        >
          {{ section.label }}
        </a>
      </nav>
      <div class="ui-gallery-nav-foot">
        <ThemeToggle />
      </div>
    </aside>

    <main class="ui-gallery-main">
      <section id="foundations" class="ui-gallery-section">
        <header class="ui-gallery-section-head">
          <h2>Foundations</h2>
          <p>Semantic color and type tokens used across the app.</p>
        </header>

        <h3 class="ui-gallery-subhead">Color</h3>
        <p class="ui-gallery-token-hint">
          Stone for structure, amber only when the tool is attending (focus,
          selection, comments, primary actions).
        </p>
        <div class="ui-gallery-swatches">
          <div
            v-for="token in colorTokens"
            :key="token.name"
            class="ui-gallery-swatch"
          >
            <div
              class="ui-gallery-swatch-chip"
              :style="{ background: token.swatch }"
            />
            <div class="ui-gallery-swatch-meta">
              <span class="ui-gallery-swatch-group">{{ token.group }}</span>
              <code>{{ token.name }}</code>
            </div>
          </div>
        </div>

        <h3 class="ui-gallery-subhead">Type</h3>
        <div class="ui-gallery-type">
          <p class="ui-gallery-type-ui">
            UI — Source Sans 3. Menus, fields, chrome.
          </p>
          <p class="ui-gallery-type-prose">
            Prose — Literata. Long-form writing in the editor.
          </p>
          <p class="ui-gallery-type-heading">
            Heading — Young Serif. Chapter titles and display.
          </p>
          <p class="ui-gallery-type-mono">
            Mono — SF Mono / ui-monospace. Paths and code.
          </p>
        </div>
      </section>

      <section id="selection" class="ui-gallery-section">
        <header class="ui-gallery-section-head">
          <h2>Selection</h2>
          <p>
            Editor and form fields share
            <code>--selection</code>
            — native selection in the manuscript, painted rects in inputs
            (WKWebView native wash suppressed).
          </p>
        </header>

        <div class="ui-gallery-selection-row">
          <div class="ui-gallery-selection-card">
            <p class="ui-gallery-caption">Editor plate</p>
            <p class="ui-gallery-selection-prose">
              <span class="ui-gallery-selection-mark"
                >So what do we do now?</span
              >
              Heather spoke for everyone.
            </p>
          </div>
          <div class="ui-gallery-selection-card">
            <p class="ui-gallery-caption">Form field — select the title</p>
            <ZwInput :id="nothing" v-model="title" :label="just('Title')" />
          </div>
        </div>
      </section>

      <section id="buttons" class="ui-gallery-section">
        <header class="ui-gallery-section-head">
          <h2>Buttons</h2>
          <p>
            <code>ZwButton</code>
            — ghost default, amber primary, danger for destructive actions.
          </p>
        </header>

        <div class="ui-gallery-row">
          <ZwButton>Cancel</ZwButton>
          <ZwButton variant="primary">Save</ZwButton>
          <ZwButton variant="danger">Delete</ZwButton>
          <ZwButton size="sm">Small</ZwButton>
          <ZwButton variant="primary" size="sm">Small primary</ZwButton>
        </div>
      </section>

      <section id="forms" class="ui-gallery-section">
        <header class="ui-gallery-section-head">
          <h2>Forms</h2>
          <p>
            Contenteditable-backed
            <code>ZwInput</code>
            /
            <code>ZwTextarea</code>
            (Tauri-safe selection), plus select and checkbox.
          </p>
        </header>

        <div class="ui-gallery-stack">
          <ZwFieldRow>
            <ZwInput :id="nothing" v-model="title" :label="just('Title')" />
            <ZwInput
              :id="nothing"
              v-model="subtitle"
              :label="just('Subtitle')"
              placeholder="Optional"
            />
          </ZwFieldRow>

          <ZwSelect
            :id="nothing"
            v-model="printSize"
            :label="just('Print size')"
            :options="just(printOptions)"
          />

          <ZwTextarea
            :id="nothing"
            v-model="notes"
            :label="just('Description')"
            :rows="3"
            placeholder="A few lines…"
          />

          <div class="zw-field">
            <span class="zw-field-label">Running heads</span>
            <div class="ui-gallery-checks">
              <ZwCheckbox v-model="showTitle">
                Show book title (recto)
              </ZwCheckbox>
              <ZwCheckbox v-model="showAuthor">
                Show author (verso)
              </ZwCheckbox>
              <ZwCheckbox disabled>Disabled option</ZwCheckbox>
            </div>
          </div>
        </div>
      </section>

      <section id="menus" class="ui-gallery-section">
        <header class="ui-gallery-section-head">
          <h2>Menus</h2>
          <p>
            <code>ZwMenu</code>
            with items and separators — border surfaces, no halo chrome.
          </p>
        </header>

        <ZwMenu v-model:open="menuOpen" align="start" :menu-label="nothing">
          <template #trigger="{ toggle, open }">
            <ZwButton
              :aria-expanded="open"
              aria-haspopup="menu"
              @click="toggle"
            >
              Open menu
            </ZwButton>
          </template>
          <ZwMenuItem
            label="Rename…"
            :shortcut="nothing"
            :title="nothing"
            @select="menuOpen = false"
          />
          <ZwMenuItem
            label="Duplicate"
            :shortcut="nothing"
            :title="nothing"
            @select="menuOpen = false"
          />
          <ZwMenuSeparator />
          <ZwMenuItem
            label="Delete"
            :shortcut="nothing"
            :title="nothing"
            @select="menuOpen = false"
          />
        </ZwMenu>
      </section>

      <section id="dialogs" class="ui-gallery-section">
        <header class="ui-gallery-section-head">
          <h2>Dialogs</h2>
          <p>
            <code>ZwDialog</code>
            — teleported shell with Escape, focus trap, cozy or sheet layout.
          </p>
        </header>

        <ZwButton variant="primary" @click="dialogOpen = true">
          Open dialog
        </ZwButton>

        <ZwDialog
          :open="dialogOpen"
          title="New file"
          :description="just('Add a document to this project.')"
          @close="dialogOpen = false"
        >
          <ZwInput
            :id="nothing"
            v-model="dialogName"
            :label="just('File name')"
            @keydown.enter.prevent="dialogOpen = false"
          />
          <template #footer>
            <ZwButton @click="dialogOpen = false">Cancel</ZwButton>
            <ZwButton variant="primary" @click="dialogOpen = false">
              Create
            </ZwButton>
          </template>
        </ZwDialog>
      </section>
    </main>
  </div>
</template>

<style>
.ui-gallery {
  display: grid;
  grid-template-columns: 14rem minmax(0, 1fr);
  min-height: 100%;
  color: var(--text);
  font-family: var(--font-ui);
}

.ui-gallery-nav {
  position: sticky;
  top: 0;
  align-self: start;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.35rem 1rem 1.25rem 1.25rem;
  border-right: 1px solid var(--line);
  background: var(--bg-raised);
  box-sizing: border-box;
}

.ui-gallery-brand h1 {
  margin: 0.15rem 0 0.4rem;
  font-family: var(--font-heading);
  font-size: 1.35rem;
  font-weight: 600;
  color: var(--text-strong);
  letter-spacing: -0.02em;
}

.ui-gallery-kicker {
  margin: 0;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.ui-gallery-lede {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--text-muted);
}

.ui-gallery-lede code,
.ui-gallery-section-head code,
.ui-gallery-section p code {
  font-family: var(--font-mono);
  font-size: 0.78em;
  padding: 0.05em 0.28em;
  border-radius: 0.2rem;
  background: color-mix(in oklch, var(--bg) 65%, var(--bg-raised));
  color: var(--text);
}

.ui-gallery-nav nav {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.ui-gallery-nav-link {
  padding: 0.35rem 0.45rem;
  border-radius: 0.3rem;
  color: var(--text-muted);
  text-decoration: none;
  font-size: 0.88rem;
}

.ui-gallery-nav-link:hover {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.ui-gallery-nav-foot {
  margin-top: auto;
}

.ui-gallery-main {
  padding: 1.75rem 2rem 3rem;
  max-width: 52rem;
}

.ui-gallery-section {
  padding: 1.5rem 0 2rem;
  border-bottom: 1px solid var(--line);
}

.ui-gallery-section:last-child {
  border-bottom: 0;
}

.ui-gallery-section-head {
  margin-bottom: 1.1rem;
}

.ui-gallery-section-head h2 {
  margin: 0 0 0.35rem;
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-strong);
}

.ui-gallery-section-head p {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.45;
  color: var(--text-muted);
  max-width: 40rem;
}

.ui-gallery-subhead {
  margin: 1.25rem 0 0.65rem;
  font-size: 0.68rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
  font-weight: 600;
}

.ui-gallery-token-hint {
  margin: -0.25rem 0 0.75rem;
  font-size: 0.82rem;
  line-height: 1.4;
  color: var(--text-muted);
  max-width: 36rem;
}

.ui-gallery-swatches {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(9.5rem, 1fr));
  gap: 0.65rem;
}

.ui-gallery-swatch {
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  overflow: hidden;
  background: var(--bg-raised);
}

.ui-gallery-swatch-chip {
  height: 2.4rem;
  border-bottom: 1px solid var(--line);
}

.ui-gallery-swatch-meta {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.4rem 0.5rem;
}

.ui-gallery-swatch-group {
  font-size: 0.62rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.ui-gallery-swatch code {
  display: block;
  font-family: var(--font-mono);
  font-size: 0.68rem;
  color: var(--text);
  word-break: break-all;
}

.ui-gallery-type {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  background: var(--bg-raised);
}

.ui-gallery-type p {
  margin: 0;
  color: var(--text-strong);
}

.ui-gallery-type-ui {
  font-family: var(--font-ui);
  font-size: 0.95rem;
}

.ui-gallery-type-prose {
  font-family: var(--font-prose);
  font-size: 1.05rem;
  line-height: 1.55;
}

.ui-gallery-type-heading {
  font-family: var(--font-heading);
  font-size: 1.35rem;
  font-weight: 600;
}

.ui-gallery-type-mono {
  font-family: var(--font-mono);
  font-size: 0.85rem;
}

.ui-gallery-selection-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
}

@media (max-width: 720px) {
  .ui-gallery {
    grid-template-columns: 1fr;
  }

  .ui-gallery-nav {
    position: relative;
    height: auto;
    border-right: 0;
    border-bottom: 1px solid var(--line);
  }

  .ui-gallery-selection-row {
    grid-template-columns: 1fr;
  }
}

.ui-gallery-selection-card {
  padding: 0.85rem 1rem 1rem;
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  background: var(--bg-raised);
}

.ui-gallery-caption {
  margin: 0 0 0.55rem;
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.ui-gallery-selection-prose {
  margin: 0;
  font-family: var(--font-prose);
  font-size: 1rem;
  line-height: 1.55;
  color: var(--text);
}

.ui-gallery-selection-mark {
  background: var(--selection);
  border-radius: 0.05rem;
}

.ui-gallery-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.ui-gallery-stack {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  max-width: 28rem;
}

.ui-gallery-checks {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.15rem 0 0.1rem;
}
</style>
